import Portal from '../Portal';
import React, { MouseEvent, useContext, useRef, useState } from 'react';
import { DeviceDetailsContext } from '../pages/devices/device-details';
import styles from './ModalImage.module.scss';
import { classnames } from "@bem-react/classnames";
import { Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { ReactComponent as CloseIcon } from '../../assets/svg/close-icon.svg';
// import { toast } from "react-toastify";
import { IconTypes, iFullStoreState, iIcon } from '../../shared/interfaces';
import { ICON_NAMES } from '../../shared/constants';
import ModalActionsComponent from '../menus/modal/modal-actions';
import ReactFileReader from 'react-file-reader';
import * as devicedb from '../../shared/db/devices-db';
import * as generaldb from '../../shared/db/general-db';
import { useRedux } from '../../states/redux-state';
import useOnClickOutside from '../../hooks/use-outside-click';
import ClearModal from '../ClearModal/ClearModal';
import { useSelector } from 'react-redux';

interface ModalImageProps {
  closeImageHandler: (e?: MouseEvent<HTMLButtonElement>) => void;
  imageSrc: string;
  isNoImage?: boolean;
}

const ModalImage: React.FC<ModalImageProps> = ({ closeImageHandler, imageSrc, isNoImage }) => {
  const { deviceImage, setDeviceImage, deviceId } = useContext(DeviceDetailsContext);
  const authUser = useRedux((state: iFullStoreState) => state.auth.user);
  const device = useSelector<iFullStoreState, any>((state) =>
    deviceId ? state.devicesData.devicesDetails.get(deviceId) : undefined
  );
  const overlayRef = useRef<HTMLDivElement>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  useOnClickOutside(overlayRef, closeImageHandler);

  const iconType = device?.icon?.type;
  const hasUrl = !!device?.icon?.url;
  const hasFa = !!device?.icon?.fa;
  const hasSvgName = !!device?.icon?.svgIconName;

  const isDefaultImagePath = imageSrc?.includes('no_car_image') || imageSrc?.includes('images/no_car_image');
  const isUserUploadedImage = !isDefaultImagePath && (
    iconType === IconTypes.UserSvgIcon ||
    iconType === 2 ||
    (hasUrl && !hasFa && !hasSvgName) ||
    (imageSrc && !isDefaultImagePath)
  );

  const apiBaseUrl = process.env.REACT_APP_API_URL;
  const encodedImageSrc = encodeURIComponent(imageSrc);
  const proxyImageSrc = `${apiBaseUrl}/api/image/proxy?url=${encodedImageSrc}`;

  const downloadImageHandler = async () => {
    try {
      if (!proxyImageSrc || typeof proxyImageSrc !== 'string') {
        throw new Error('Invalid image source');
      }

      let url: URL;
      try {
        url = new URL(proxyImageSrc);
      } catch {
        throw new Error('Invalid URL format');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch(proxyImageSrc, {
        method: 'GET',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.startsWith('image/')) {
        throw new Error('Response is not an image');
      }

      const blob = await response.blob();

      const getFileExtension = (contentType) => {
        const typeMap = {
          'image/jpeg': '.jpg',
          'image/jpg': '.jpg',
          'image/png': '.png',
          'image/webp': '.webp',
          'image/gif': '.gif',
          'image/svg+xml': '.svg',
          'image/bmp': '.bmp',
          'image/tiff': '.tiff',
          'image/avif': '.avif',
          'image/heic': '.heic',
          'image/heif': '.heif',
          'image/ico': '.ico',
          'image/x-icon': '.ico',
          'image/vnd.microsoft.icon': '.ico'
        };
        return typeMap[contentType] || '.jpg';
      };

      const extension = getFileExtension(contentType);
      const fileName = `image_${Date.now()}${extension}`;

      if ('showSaveFilePicker' in window) {
        try {
          const fileHandle = await (window as any).showSaveFilePicker({
            suggestedName: fileName,
            types: [
              {
                description: 'Image files',
                accept: {
                  'image/jpeg': ['.jpg', '.jpeg'],
                  'image/png': ['.png'],
                  'image/webp': ['.webp'],
                  'image/gif': ['.gif'],
                  'image/svg+xml': ['.svg'],
                  'image/bmp': ['.bmp'],
                  'image/tiff': ['.tiff', '.tif'],
                  'image/avif': ['.avif'],
                  'image/heic': ['.heic', '.heif'],
                  'image/ico': ['.ico'],
                  'image/x-icon': ['.ico'],
                  'image/vnd.microsoft.icon': ['.ico']
                },
              },
            ],
          });

          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();

          return true;
        } catch (saveError) {
          if (saveError.name === 'AbortError') {
            return false;
          }
          throw saveError;
        }
      } else {
        const objectUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = fileName;
        a.style.display = 'none';

        document.body.appendChild(a);

        a.click();

        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(objectUrl);
        }, 100);

        return true;
      }
    } catch (error) {
      console.error('Download failed:', error?.message || 'Unknown error');

      let userMessage = 'Failed to download image. ';

      if (error?.name === 'TypeError' && error?.message?.includes('CORS')) {
        userMessage += 'The image cannot be downloaded due to cross-origin restrictions.';
      } else if (error?.name === 'AbortError') {
        userMessage += 'Download was cancelled or timed out.';
      } else if (error?.message?.includes('HTTP error')) {
        userMessage += 'The image could not be found or is not accessible.';
      } else if (error?.message?.includes('not an image')) {
        userMessage += 'The provided URL does not point to a valid image.';
      } else {
        userMessage += 'Please try again or check the image URL.';
      }

      // toast.error(userMessage, { position: toast.POSITION.TOP_RIGHT });

      return false;
    }
  };

  const updateDeviceImage = async () => {
    try {
      await devicedb.updateDeviceDetails(authUser)(
        deviceId,
        deviceImage
      )
    } catch (e) {
      console.log('error', e);
    }
    closeModal();
  }

  const handleFiles = async (files: Array<File>) => {
    const [file] = files;
    if (!file || !(typeof file == 'object')) return;
    let icon: iIcon;
    if ('icon' in file) {
      icon = {
        type: IconTypes.FaIcon,
        fa: file as any,
      };
    } else if ('name' in file && ICON_NAMES[file.name]) {
      icon = {
        type: IconTypes.SvgIcon,
        svgIconName: file.name as string,
      };
    } else {
      icon = {
        type: IconTypes.UserSvgIcon,
        url: await generaldb.saveIcon(file as File),
      };
    }

    setDeviceImage({ ...deviceImage, icon });
  };

  const cancelDeviceImageUpdate = () => {
    setDeviceImage({});
  }

  const handleDeleteImage = async () => {
    if (!deviceId) {
      // toast.error('Device ID is missing', { position: toast.POSITION.TOP_RIGHT });
      setShowDeleteConfirm(false);
      return;
    }

    try {
      const newIcon = { type: IconTypes.SvgIcon, svgIconName: 'truck' };

      await devicedb.updateDeviceDetails(authUser)(
        deviceId,
        { icon: newIcon }
      );

      setDeviceImage({});
      // toast.success('Image deleted successfully', { position: toast.POSITION.TOP_RIGHT });
      setShowDeleteConfirm(false);
      closeModal();
    } catch (e) {
      console.error('Error deleting image:', e);
      // toast.error(`Failed to delete image: ${e?.message || 'Unknown error'}`, { position: toast.POSITION.TOP_RIGHT });
      setShowDeleteConfirm(false);
    }
  };

  const closeModal = () => {
    cancelDeviceImageUpdate();
    closeImageHandler();
  }

  return (
    <>
      {!showDeleteConfirm && (
        <Portal>
          <div className={styles.modal__overlay}>
            <div className={classnames(styles.modal__block)} onClick={(e) => e.stopPropagation()} ref={overlayRef}>
            <div className={styles.imageModal}>
              <div className={styles.buttons}>
                {deviceId && (
                  <ReactFileReader handleFiles={handleFiles}>
                    <Tooltip title={'Add image'} placement='top'>
                      <button className={styles.downloadImageButton}>
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    </Tooltip>
                  </ReactFileReader>
                )}
                {!isNoImage && deviceId && (
                  <Tooltip title={isUserUploadedImage ? 'Delete Image' : 'Delete not available for default icons'} placement='top'>
                    <button
                      onClick={() => {
                        if (isUserUploadedImage) {
                          setShowDeleteConfirm(true);
                        } else {
                          // toast.info('Only user-uploaded images can be deleted', { position: toast.POSITION.TOP_RIGHT });
                        }
                      }}
                      className={styles.downloadImageButton}
                      disabled={!isUserUploadedImage}
                      style={{ opacity: isUserUploadedImage ? 1 : 0.5, cursor: isUserUploadedImage ? 'pointer' : 'not-allowed' }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </Tooltip>
                )}
                {!isNoImage && <Tooltip title={'Download Image'} placement='top'>
                  <button onClick={downloadImageHandler} className={styles.downloadImageButton}>
                    <FontAwesomeIcon icon={faDownload} />
                  </button>
                </Tooltip>}
                <Tooltip title={'Close Modal'} placement='top'>
                  <button onClick={closeImageHandler} className={styles.closeModalButton}>
                    <CloseIcon />
                  </button>
                </Tooltip>
              </div>
              <img
                className={styles.image}
                src={
                  deviceImage && deviceImage?.icon && deviceImage.icon.url
                    ? deviceImage.icon.url
                    : imageSrc
                }
              />
              {Object.keys(deviceImage).length ? (
                <ModalActionsComponent
                  buttons={[
                    { click: cancelDeviceImageUpdate, title: 'Cancel' },
                    { click: updateDeviceImage, title: 'Update', primary: true },
                  ]}
                />
              ) : null}
            </div>
          </div>
        </div>
      </Portal>
      )}

      <ClearModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteImage}
        title="Delete Image"
        bodyText="Are you sure you want to delete this image? This action cannot be undone."
      />
    </>
  )
}

export default ModalImage;
